// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";

// Model
import { ArmedForcesProofModel } from "./ArmedForcesProofModel";
import { AuditedAccountModel } from "./AuditedAccountModel";
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";

// Dto
import { EmploymentDetailsDTO } from "Globals/Models/DTO/EmploymentDetailsDTO";
import { ArmedForcesProofDTO } from "Globals/Models/DTO/ArmedForcesProofDTO";
import { AuditedAccountDTO } from "Globals/Models/DTO/AuditedAccountDTO";
export class EmploymentDetailsModel extends ModelBase<EmploymentDetailsModel, EmploymentDetailsAndRelated> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string | null = "";
    @MobX.observable public applicantId: string | null = "";
    @MobX.observable public regularInArmedForces: boolean = true;
    @MobX.observable public employmentStatus: number | null = EmploymentStatusEnum.Empty;
    @MobX.observable public occupation: string | null = "";
    @MobX.observable public worksFullTimeNotPartTime: boolean = true;
    @MobX.observable public employerName: string | null = "";
    @MobX.observable public employerTelephone: string | null = "";
    @MobX.observable public employerAddressLine1: string | null = "";
    @MobX.observable public employerAddressLine2: string | null = "";
    @MobX.observable public employerAddressLine3: string | null = "";
    @MobX.observable public employerCity: string | null = "";
    @MobX.observable public employerCounty: string | null = "";
    @MobX.observable public employerPostcode: string | null = "";
    @MobX.observable public yearsAtCompany: number | null = 0;
    @MobX.observable public monthsAtCompany: number | null = 0;
    @MobX.observable public onProbation: boolean = true;
    @MobX.observable public probationLength: number | null = 0;
    @MobX.observable public contractStartDate: Date | null = null;
    @MobX.observable public contractEndDate: Date | null = null;
    @MobX.observable public yearsSelfEmployed: number | null = 0;
    @MobX.observable public monthSelfEmployed: number | null = 0;
    @MobX.observable public canProvideThreeYearsAuditAccounts: boolean = true;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @MobX.observable public armedForcesProofModels: ArmedForcesProofModel[] = [];
    @MobX.observable public auditedAccountModels: AuditedAccountModel[] = [];

    @MobX.action
    public fromDto = (model: EmploymentDetailsAndRelated): void => {
        if (model !== null && model !== undefined) {
            this.id = model.employmentDetails.id;
            this.formId = model.employmentDetails.formId;
            this.applicantId = model.employmentDetails.applicantId;
            this.regularInArmedForces = model.employmentDetails.regularInArmedForces;
            this.employmentStatus = model.employmentDetails.employmentStatus;
            this.occupation = model.employmentDetails.occupation;
            this.worksFullTimeNotPartTime = model.employmentDetails.worksFullTimeNotPartTime;
            this.employerName = model.employmentDetails.employerName;
            this.employerTelephone = model.employmentDetails.employerTelephone;
            this.employerAddressLine1 = model.employmentDetails.employerAddressLine1;
            this.employerAddressLine2 = model.employmentDetails.employerAddressLine2;
            this.employerAddressLine3 = model.employmentDetails.employerAddressLine3;
            this.employerCity = model.employmentDetails.employerCity;
            this.employerCounty = model.employmentDetails.employerCounty;
            this.employerPostcode = model.employmentDetails.employerPostcode;
            this.yearsAtCompany = model.employmentDetails.yearsAtCompany;
            this.monthsAtCompany = model.employmentDetails.monthsAtCompany;
            this.onProbation = model.employmentDetails.onProbation;
            this.probationLength = model.employmentDetails.probationLength;
            this.contractStartDate = model.employmentDetails.contractStartDate;
            this.contractEndDate = model.employmentDetails.contractEndDate;
            this.yearsSelfEmployed = model.employmentDetails.yearsSelfEmployed;
            this.monthSelfEmployed = model.employmentDetails.monthSelfEmployed;
            this.canProvideThreeYearsAuditAccounts = model.employmentDetails.canProvideThreeYearsAuditAccounts;
            this.createdDate = model.employmentDetails.createdDate;
            this.isDeleted = model.employmentDetails.isDeleted;

            // Load armed forces proof
            model.armedForcesProofModels.forEach((armedForcesProof) => {
                let localArmedForcesProofModels: ArmedForcesProofModel = new ArmedForcesProofModel();
                localArmedForcesProofModels.fromDto(armedForcesProof);
                this.armedForcesProofModels.push(localArmedForcesProofModels);
            });

            // Load audited accounts
            model.auditedAccountModels.forEach((auditedAccount) => {
                let localAuditedAccountModels: AuditedAccountModel = new AuditedAccountModel();
                localAuditedAccountModels.fromDto(auditedAccount);
                this.auditedAccountModels.push(localAuditedAccountModels);
            });
        }
    };

    @MobX.action
    public fromEmploymentDetailsDto = (model: EmploymentDetailsDTO): void => {
        if (model !== null && model !== undefined) {
            this.id = model.id;
            this.formId = model.formId;
            this.applicantId = model.applicantId;
            this.regularInArmedForces = model.regularInArmedForces;
            this.employmentStatus = model.employmentStatus;
            this.occupation = model.occupation;
            this.worksFullTimeNotPartTime = model.worksFullTimeNotPartTime;
            this.employerName = model.employerName;
            this.employerTelephone = model.employerTelephone;
            this.employerAddressLine1 = model.employerAddressLine1;
            this.employerAddressLine2 = model.employerAddressLine2;
            this.employerAddressLine3 = model.employerAddressLine3;
            this.employerCity = model.employerCity;
            this.employerCounty = model.employerCounty;
            this.employerPostcode = model.employerPostcode;
            this.yearsAtCompany = model.yearsAtCompany;
            this.monthsAtCompany = model.monthsAtCompany;
            this.onProbation = model.onProbation;
            this.probationLength = model.probationLength;
            this.contractStartDate = model.contractStartDate;
            this.contractEndDate = model.contractEndDate;
            this.yearsSelfEmployed = model.yearsSelfEmployed;
            this.monthSelfEmployed = model.monthSelfEmployed;
            this.canProvideThreeYearsAuditAccounts = model.canProvideThreeYearsAuditAccounts;
            this.createdDate = model.createdDate;
            this.isDeleted = model.isDeleted;
        }
    };

    @MobX.action
    public toDto = (): EmploymentDetailsDTO => {
        let model: EmploymentDetailsDTO = {
            id: this.id,
            formId: this.formId,
            applicantId: this.applicantId,
            regularInArmedForces: this.regularInArmedForces,
            employmentStatus: this.employmentStatus,
            occupation: this.occupation,
            worksFullTimeNotPartTime: this.worksFullTimeNotPartTime,
            employerName: this.employerName,
            employerTelephone: this.employerTelephone,
            employerAddressLine1: this.employerAddressLine1,
            employerAddressLine2: this.employerAddressLine2,
            employerAddressLine3: this.employerAddressLine3,
            employerCity: this.employerCity,
            employerCounty: this.employerCounty,
            employerPostcode: this.employerPostcode,
            yearsAtCompany: this.yearsAtCompany,
            monthsAtCompany: this.monthsAtCompany,
            onProbation: this.onProbation,
            probationLength: this.probationLength,
            contractStartDate: this.contractStartDate,
            contractEndDate: this.contractEndDate,
            yearsSelfEmployed: this.yearsSelfEmployed,
            monthSelfEmployed: this.monthSelfEmployed,
            canProvideThreeYearsAuditAccounts: this.canProvideThreeYearsAuditAccounts,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };
}

// Extend interface to accept new form sections
export interface EmploymentDetailsAndRelated {
    employmentDetails: EmploymentDetailsDTO;
    armedForcesProofModels: ArmedForcesProofDTO[];
    auditedAccountModels: AuditedAccountDTO[];
}
